<template>
  <Transition :name="transitionName">
    <div
      class="base-overlay"
      :class="positionClass"
      :role="config.pausesVideo ? 'dialog' : 'status'"
      :aria-label="ariaLabel"
      :aria-modal="config.pausesVideo || undefined"
    >
      <div class="base-overlay__content">
        <button
          v-if="config.isDismissible"
          class="base-overlay__close"
          :aria-label="'Close'"
          @click="$emit('dismiss')"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>

        <component :is="overlayComponent" :payload="config.payload" @submit="(data: unknown) => $emit('submit', data)" />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, type Component, type PropType } from 'vue'
import type { OverlayConfig } from '../../../types/embed'
import CtaOverlay from './types/CtaOverlay.vue'
import RatingOverlay from './types/RatingOverlay.vue'
import InfoOverlay from './types/InfoOverlay.vue'
import RecommendationsOverlay from './types/RecommendationsOverlay.vue'

const props = defineProps({
  config: {
    type: Object as PropType<OverlayConfig>,
    required: true,
  },
})

defineEmits<{
  dismiss: []
  submit: [data: unknown]
}>()

const typeComponentMap: Record<string, Component> = {
  cta: CtaOverlay,
  rating: RatingOverlay,
  info: InfoOverlay,
  recommendations: RecommendationsOverlay,
}

const overlayComponent = computed(() => {
  return typeComponentMap[props.config.overlayType] ?? InfoOverlay
})

const ariaLabel = computed(() => {
  const typeLabels: Record<string, string> = {
    cta: 'Call to action',
    rating: 'Rating prompt',
    recommendations: 'Recommendations',
    custom_html: 'Custom content',
    info: 'Information',
  }
  return typeLabels[props.config.overlayType] ?? 'Overlay'
})

const transitionName = computed(() => {
  const animationMap: Record<string, string> = {
    fade: 'overlay-fade',
    slide_up: 'overlay-slide-up',
    slide_down: 'overlay-slide-down',
    scale: 'overlay-scale',
    none: '',
  }
  return animationMap[props.config.animation] ?? 'overlay-fade'
})

const positionClass = computed(() => {
  return `base-overlay--${props.config.position}`
})
</script>

<style scoped lang="scss">
.base-overlay {
  position: absolute;
  pointer-events: auto;
  display: flex;
  max-width: 90%;
  max-height: 80%;

  // Position variants
  &--center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &--top_left {
    top: 16px;
    left: 16px;
  }

  &--top_right {
    top: 16px;
    right: 16px;
  }

  &--top_center {
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
  }

  &--bottom_left {
    bottom: 80px;
    left: 16px;
  }

  &--bottom_right {
    bottom: 80px;
    right: 16px;
  }

  &--bottom_center {
    bottom: 80px;
    left: 50%;
    transform: translateX(-50%);
  }

  &--fullscreen {
    inset: 0;
    max-width: none;
    max-height: none;
    width: 100%;
    height: 100%;

    .base-overlay__content {
      width: 100%;
      height: 100%;
      border-radius: 0;
      border: none;
      // Near-opaque: at 0.85 the video showed through behind the card's text.
      background: linear-gradient(
        180deg,
        rgba(18, 18, 22, 0.96) 0%,
        rgba(12, 12, 16, 0.97) 100%
      );
      backdrop-filter: blur(8px);
      box-shadow: none;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px 32px;
    }

    .base-overlay__close {
      top: 20px;
      right: 20px;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.08);

      svg {
        width: 20px;
        height: 20px;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.16);
      }
    }
  }

  &__content {
    position: relative;
    background: linear-gradient(
      165deg,
      rgba(38, 38, 42, 0.95) 0%,
      rgba(24, 24, 28, 0.97) 100%
    );
    backdrop-filter: blur(20px) saturate(1.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 28px 24px 24px;
    color: #fff;
    box-shadow:
      0 4px 6px rgba(0, 0, 0, 0.15),
      0 12px 40px rgba(0, 0, 0, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);
    min-width: 220px;
  }

  &__close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 28px;
    height: 28px;
    background: rgba(255, 255, 255, 0.06);
    border: none;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 150ms ease, background-color 150ms ease;

    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.14);
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 2px;
    }
  }
}

// Transitions
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 300ms ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.overlay-slide-up-enter-active,
.overlay-slide-up-leave-active {
  transition: opacity 300ms ease, transform 300ms ease;
}

.overlay-slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.overlay-slide-up-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.overlay-slide-down-enter-active,
.overlay-slide-down-leave-active {
  transition: opacity 300ms ease, transform 300ms ease;
}

.overlay-slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.overlay-slide-down-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.overlay-scale-enter-active,
.overlay-scale-leave-active {
  transition: opacity 300ms ease, transform 300ms ease;
}

.overlay-scale-enter-from {
  opacity: 0;
  transform: scale(0.85);
}

.overlay-scale-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

// Override transitions for centered overlays (preserve transform)
.base-overlay--center {
  .overlay-slide-up-enter-from {
    transform: translate(-50%, calc(-50% + 20px));
  }

  .overlay-slide-up-leave-to {
    transform: translate(-50%, calc(-50% - 20px));
  }

  .overlay-slide-down-enter-from {
    transform: translate(-50%, calc(-50% - 20px));
  }

  .overlay-slide-down-leave-to {
    transform: translate(-50%, calc(-50% + 20px));
  }

  .overlay-scale-enter-from,
  .overlay-scale-leave-to {
    transform: translate(-50%, -50%) scale(0.85);
  }
}

// Light theme adjustments
[data-vp-theme='light'] .base-overlay {
  &__content {
    background: linear-gradient(
      165deg,
      rgba(250, 250, 252, 0.97) 0%,
      rgba(240, 240, 244, 0.98) 100%
    );
    border-color: rgba(0, 0, 0, 0.1);
    color: #000000;
    box-shadow:
      0 4px 6px rgba(0, 0, 0, 0.08),
      0 12px 40px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  &__close {
    background: rgba(0, 0, 0, 0.05);
    // 0.4 was ~2.8:1, under the 3:1 a control's icon needs; 0.6 is ~5.3:1.
    color: rgba(0, 0, 0, 0.6);

    &:hover {
      color: #000000;
      background: rgba(0, 0, 0, 0.1);
    }

    &:focus-visible {
      outline-color: #000000;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .overlay-fade-enter-active,
  .overlay-fade-leave-active,
  .overlay-slide-up-enter-active,
  .overlay-slide-up-leave-active,
  .overlay-slide-down-enter-active,
  .overlay-slide-down-leave-active,
  .overlay-scale-enter-active,
  .overlay-scale-leave-active {
    transition-duration: 1ms;
  }
}

@media (max-width: 768px) {
  .base-overlay {
    &--bottom_left,
    &--bottom_right,
    &--bottom_center {
      bottom: 60px;
    }

    &__content {
      padding: 14px;
      min-width: 160px;
      border-radius: 8px;
    }
  }
}
</style>
